import { Link } from "react-router-dom";
import { Reveal, SectionHead, PageHeader } from "@/components/common/Reveal";
import { CapabilityTile } from "@/components/sections/CapabilitiesGrid";
import { CtaBand } from "@/components/sections/CtaBand";
import { CAPABILITIES, PROCESS, SECTORS, PROJECTS } from "@/data/site";

const bySlug = (s) => PROJECTS.find((p) => p.slug === s);

const CapabilityRow = ({ c, i }) => (
  <div className="grid gap-6 border-t border-canvas-line py-10 lg:grid-cols-12" data-testid={`capability-row-${i}`}>
    <div className="lg:col-span-5"><CapabilityTile c={c} i={i} /></div>
    <Reveal className="lg:col-span-6 lg:col-start-7 lg:pt-8">
      <p className="label-mono text-brand">Delivered on</p>
      <ul className="mt-5 divide-y divide-canvas-line border-y border-canvas-line">
        {c.projects.map((s) => {
          const p = bySlug(s);
          return (
            <li key={s}>
              <Link to={`/projects/${s}`} className="group flex items-center justify-between py-4" data-testid={`capability-${i}-project-${s}`}>
                <span className="font-display text-xl font-semibold uppercase group-hover:text-brand">{p.name}</span>
                <span className="label-mono text-steel">{p.units ? `${p.units} units` : p.ref}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Reveal>
  </div>
);

const Process = () => (
  <section className="bg-ink text-canvas cad-grid-light" data-testid="capabilities-process">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <SectionHead tone="dark" label="Delivery process" title="Design → Implement → Manage" />
      <div className="mt-16 grid gap-px bg-canvas/15 md:grid-cols-3">
        {PROCESS.map((s, i) => (
          <Reveal key={s.step} delay={i * 0.1} className="bg-ink p-8 sm:p-10">
            <span className="font-display text-7xl font-extrabold text-brand-soft">{s.step}</span>
            <h3 className="mt-6 font-display text-3xl font-bold uppercase">{s.title}</h3>
            <p className="mt-4 leading-relaxed text-canvas/75">{s.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Sectors = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="capabilities-sectors">
    <SectionHead label="Sectors" title="Where we work" text="Our completed and ongoing schemes are residential. We also bring the same mechanical and renewable capability to commercial, mixed-use and public sector projects." />
    <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {SECTORS.map((s, i) => {
        const ex = s.example ? bySlug(s.example) : null;
        return (
          <Reveal key={s.title} delay={(i % 3) * 0.08} className="h-full">
            <div className="flex h-full flex-col border border-canvas-line p-7 sm:p-8" data-testid={`sector-${i}`}>
              <span className="label-mono text-steel">S-{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-8 font-display text-3xl font-bold uppercase leading-none">{s.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-steel sm:text-base">{s.text}</p>
              {ex && (
                <Link to={`/projects/${ex.slug}`} className="label-mono mt-6 text-brand hover:text-leaf" data-testid={`sector-${i}-example`}>
                  e.g. {ex.name} →
                </Link>
              )}
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export default function Capabilities() {
  return (
    <main data-testid="capabilities-page">
      <PageHeader testid="capabilities-header" label="Capabilities" title="Mechanical & renewable services for construction"
        text="A single mechanical contractor for heating, plumbing, heat pumps and ventilation on multi-unit schemes, working to the main contractor's programme." />
      <section className="mx-auto max-w-7xl px-5 pb-12 pt-6 sm:px-8">
        {CAPABILITIES.map((c, i) => <CapabilityRow key={c.code} c={c} i={i} />)}
      </section>
      <Process />
      <Sectors />
      <CtaBand testid="capabilities-cta" />
    </main>
  );
}
